import MUITreeViewContext from '@material-ui/lab/esm/TreeView/TreeViewContext';
import MUITreeItem from '@material-ui/lab/TreeItem';
import cn from 'classnames';
import {
  useContext,
  useRef,
  useEffect,
  FC,
  ReactNode,
  Children,
  cloneElement,
} from 'react';
import * as React from 'react';
import {
  useDrag,
  useDrop,
  DragSourceMonitor,
  DropTargetMonitor,
  ConnectableElement,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { ItemTypes, DragItem } from '../DragLayer/DragItemTypes';
import useStyles from './TreeViewItem.styles';
import TreeViewContext from './TreeViewContext';
import TreeViewItemLabel from './TreeViewItemLabel';

export interface TreeViewItemProps {
  nodeId: string;
  icon: ReactNode;
  label: ReactNode;
  expandable?: boolean;
  draggable?: boolean;
  isLoading?: boolean;
  onExpansion?: (expanded: boolean) => void;
  onDrop?: (nodeIds: string[]) => void;
  onCanDrop?: (nodeIds: string[]) => boolean;
  // Injected by parent TreeViewItem
  indent?: number;
}

export const TreeViewItem: FC<TreeViewItemProps> = ({
  nodeId,
  icon,
  label,
  indent = 0,
  expandable = false,
  draggable = false,
  isLoading = false,
  onExpansion,
  onDrop,
  onCanDrop = () => !!onDrop,
  children,
}) => {
  const classes = useStyles();

  const { isSelected, isExpanded, toggleExpansion } =
    useContext(MUITreeViewContext);
  const { isDraggingGlobal, onDragStart, onDragEnd, getSelected } =
    useContext(TreeViewContext);
  const expanded = isExpanded(nodeId);

  const labelRef = useRef<HTMLElement | null>(null);

  const [{ isDragging }, dragRef, dragPreview] = useDrag({
    item: {
      type: ItemTypes.TreeViewItem,
      stack: [],
      boundingRect: { height: 0, width: 0, x: 0, y: 0 },
    },
    begin: () => {
      onDragStart();

      // Ensure the node that is being dragged is first in the drag stack.
      const stack = [nodeId, ...getSelected().filter((id) => id !== nodeId)];
      const { height, width, x, y } = labelRef.current
        ? labelRef.current.getBoundingClientRect()
        : { height: 0, width: 0, x: 0, y: 0 };

      return {
        type: ItemTypes.TreeViewItem,
        stack,
        boundingRect: { height, width, x, y },
      };
    },
    end: () => {
      onDragEnd();
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Disable the HTML5 drag preview image becuase we are rendering our
  // own custom drag layer.
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const [{ isOver }, dropRef] = useDrop({
    accept: Object.values(ItemTypes),
    drop: (item: DragItem) => {
      if (onDrop && isOver) {
        onDrop(item.stack);
      }
    },
    collect: (monitor: DropTargetMonitor) => {
      const item = monitor.getItem() as DragItem | null;
      if (!item) return { isOver: false };

      return {
        isOver: monitor.isOver({ shallow: true }) && onCanDrop(item.stack),
      };
    },
  });

  return (
    <MUITreeItem
      ref={(ref: ConnectableElement) => {
        if (draggable) {
          dragRef(ref);
        }
        if (onDrop) {
          dropRef(ref);
        }
      }}
      nodeId={nodeId}
      label={
        <TreeViewItemLabel
          ref={labelRef}
          indent={indent}
          icon={icon}
          label={label}
          isLoading={isLoading}
          isExpandable={expandable}
          isExpanded={expanded}
          onExpansion={(event) => {
            if (!expandable) return;
            event.stopPropagation();

            toggleExpansion(event, nodeId);
            if (onExpansion) {
              onExpansion(!expanded);
            }
          }}
          onClick={(event) => {
            const isSelectingMultiple =
              event.shiftKey || event.ctrlKey || event.metaKey;
            if (!expandable || isSelectingMultiple) return;

            toggleExpansion(event, nodeId);
            if (onExpansion) {
              onExpansion(!expanded);
            }
          }}
        />
      }
      classes={{
        root: cn(
          classes.root,
          (isDragging || (isDraggingGlobal && isSelected(nodeId))) &&
            classes.isDragging,
          isOver && classes.isOver,
        ),
        content: classes.content,
        selected: classes.selected,
        expanded: classes.expanded,
        iconContainer: classes.iconContainer,
        label: classes.label,
        group: classes.group,
      }}
    >
      {Children.map(children, (child) =>
        cloneElement(child as React.ReactElement<TreeViewItemProps>, {
          indent: indent + 1,
        }),
      )}
    </MUITreeItem>
  );
};

export default TreeViewItem;
